import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Plus, Check, ArrowRight, Sparkles, X } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { useBooking } from "../contexts/BookingContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getPrice(item) {
  const opt = item.rental_options?.[0];
  if (opt) {
    const p = opt.price_4_day || opt.price_8_day || opt.price || 0;
    if (p > 0) return p;
  }
  return item.price_4_day || item.price_8_day || item.price || 0;
}

export default function CompleteTheLook({ outfitId, onAddBundle, onQuickView, mainDuration, variant = "pdp" }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [showFullCollection, setShowFullCollection] = useState(false);
  const [categoryId, setCategoryId] = useState(null);
  const [itemState, setItemState] = useState({});
  const { addToCart, removeFromCart, fetchCart, discountInfo, cartGroups } = useCart();
  const booking = useBooking();

  useEffect(() => { fetchSuggestions(); }, [outfitId]);

  // Track which suggestion outfit_ids are currently in cart
  useEffect(() => {
    const cartOutfitIds = new Set();
    const cartItemMap = {};
    for (const g of cartGroups) {
      cartOutfitIds.add(g.main.outfit_id);
      cartItemMap[g.main.outfit_id] = g.main.id;
      for (const a of g.addons) {
        cartOutfitIds.add(a.outfit_id);
        cartItemMap[a.outfit_id] = a.id;
      }
    }
    setItemState(prev => {
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        next[key] = { ...next[key], added: cartOutfitIds.has(key), cartItemId: cartItemMap[key] || null };
      }
      return next;
    });
  }, [cartGroups]);

  const fetchSuggestions = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/outfits/${outfitId}/suggestions`);
      setSuggestions(res.data.suggestions || []);
      setEnabled(res.data.complete_the_look_enabled !== false && res.data.show_complete_the_look !== false);
      setShowFullCollection(res.data.addon_show_full_collection || false);
      setCategoryId(res.data.addon_category_id || null);
      const initial = {};
      (res.data.suggestions || []).forEach(s => {
        initial[s.id] = { added: false, selectedOption: s.rental_options?.[0] || null, cartItemId: null };
      });
      setItemState(initial);
    } catch (error) { console.error("Error:", error); }
    finally { setLoading(false); }
  };

  if (loading || !enabled || suggestions.length === 0) return null;

  const discount = discountInfo?.discount_percent || 0;

  const handleToggle = async (item) => {
    const st = itemState[item.id];

    if (st?.added && st?.cartItemId) {
      // Remove from cart
      await removeFromCart(st.cartItemId);
      await fetchCart();
      setItemState(prev => ({ ...prev, [item.id]: { ...prev[item.id], added: false, cartItemId: null } }));
      return;
    }

    // Add to cart
    const option = st?.selectedOption || item.rental_options?.[0];
    if (!option) return;

    const result = await addToCart({
      outfit_id: item.id,
      outfit_name: item.name,
      outfit_image: item.images?.[0],
      rental_price: option.price_4_day || option.price_8_day || option.price || getPrice(item),
      bond_amount: option.bond || item.bond_amount || 0,
      event_date: booking.eventDate || sessionStorage.getItem("mahra_event_date"),
      duration: mainDuration || booking.rentalDuration || 4,
      rental_option_id: option.id,
      rental_option_name: option.name || option.duration_label,
      delivery_method: booking.deliveryMethod || "pickup",
    });

    if (result) {
      setItemState(prev => ({ ...prev, [item.id]: { ...prev[item.id], added: true, cartItemId: result.item?.id || null } }));
      if (onAddBundle) onAddBundle({ ...item, selectedOption: option }, true);
    }
  };

  const nextTier = discountInfo?.next_tier;
  const currentDiscount = discountInfo?.discount_percent || 0;
  const isPDP = variant === "pdp";
  const displayItems = isPDP ? suggestions.slice(0, 4) : suggestions.slice(0, 6);

  // Tiered discount messaging
  const tierMessage = currentDiscount > 0 && nextTier
    ? `Saving ${currentDiscount}%! Add more for ${nextTier.next_discount}%`
    : currentDiscount > 0
    ? `${currentDiscount}% multi-item discount applied`
    : nextTier
    ? `Add ${nextTier.items_needed} more to unlock ${nextTier.next_discount}% off`
    : null;

  // ─── PDP: lighter horizontal scroll ───
  if (isPDP) {
    return (
      <section className="mt-6" data-testid="complete-the-look-pdp">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[13px] text-[#676664]/70 tracking-[0.08em] uppercase font-medium" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, fontSize: "14px" }}>
            Style Inspiration
          </h3>
          {tierMessage && (
            <span className="text-[10px] text-[#9A7B4F] flex items-center gap-1 bg-[#9A7B4F]/8 px-2.5 py-1" data-testid="tier-message-pdp">
              <Sparkles className="w-3 h-3" strokeWidth={1.5} />
              {tierMessage}
            </span>
          )}
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide" data-testid="ctl-pdp-scroll">
          {displayItems.map((item) => {
            const st = itemState[item.id] || {};
            return (
              <div key={item.id} className="flex-shrink-0 w-[120px] group" data-testid={`ctl-pdp-item-${item.id}`}>
                <div className="relative aspect-[3/4] bg-[#f5f3f0] overflow-hidden mb-1.5 cursor-pointer" onClick={() => onQuickView(item)}>
                  <img src={item.images?.[0] || ""} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                  <button
                    onClick={(e) => { e.stopPropagation(); handleToggle(item); }}
                    className={`absolute bottom-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center transition-all ${st.added ? 'bg-[#9A7B4F] text-white hover:bg-red-400' : 'bg-white/90 text-[#676664] hover:bg-[#9A7B4F] hover:text-white'}`}
                    data-testid={`ctl-pdp-toggle-${item.id}`}
                    title={st.added ? "Remove from cart" : "Add to cart"}
                  >
                    {st.added ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                  </button>
                </div>
                <p className="text-[11px] text-[#282828]/70 truncate">{item.name}</p>
              </div>
            );
          })}
        </div>
        {showFullCollection && categoryId && (
          <div className="mt-2">
            <Link to={`/catalogue?category=${categoryId}`} className="inline-flex items-center gap-1.5 text-[11px] text-[#9A7B4F]/60 hover:text-[#9A7B4F] transition-colors" data-testid="view-full-collection-link">
              View Full Collection <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        )}
      </section>
    );
  }

  // ─── Cart: wide horizontal scroll upsell ───
  return (
    <section data-testid="complete-the-look-cart">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[18px] text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
          Complete Your Look
        </h3>
        {tierMessage && (
          <span className="text-[10px] bg-[#9A7B4F]/10 text-[#9A7B4F] px-3 py-1 tracking-[0.06em] uppercase font-medium flex items-center gap-1" data-testid="tier-message-cart">
            <Sparkles className="w-3 h-3" strokeWidth={1.5} />
            {tierMessage}
          </span>
        )}
      </div>
      <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-hide" data-testid="ctl-cart-scroll">
        {displayItems.map((item) => {
          const st = itemState[item.id] || {};
          const rentalPrice = getPrice(item);
          const discountedPrice = rentalPrice * (1 - discount / 100);
          return (
            <div key={item.id} className="flex-shrink-0 w-[150px] sm:w-[170px] group" data-testid={`ctl-cart-item-${item.id}`}>
              <div className="relative aspect-[3/4] bg-[#f5f3f0] overflow-hidden mb-2 cursor-pointer" onClick={() => onQuickView && onQuickView(item)}>
                <img src={item.images?.[0] || ""} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                <button
                  onClick={(e) => { e.stopPropagation(); handleToggle(item); }}
                  className={`absolute bottom-2 right-2 w-7 h-7 rounded-full flex items-center justify-center transition-all shadow-sm ${st.added ? 'bg-[#9A7B4F] text-white hover:bg-red-400' : 'bg-white/95 text-[#676664] hover:bg-[#9A7B4F] hover:text-white'}`}
                  data-testid={`ctl-cart-toggle-${item.id}`}
                  title={st.added ? "Remove from cart" : "Add to cart"}
                >
                  {st.added ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                </button>
              </div>
              <p className="text-[12px] text-[#282828] truncate leading-snug">{item.name}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                {discount > 0 && rentalPrice > 0 && (
                  <span className="text-[11px] text-[#676664]/35 line-through">${rentalPrice.toFixed(0)}</span>
                )}
                <span className="text-[12px] text-[#9A7B4F] font-medium">
                  {rentalPrice > 0 ? `$${discountedPrice.toFixed(0)}` : "View price"}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {showFullCollection && categoryId && (
        <div className="mt-3">
          <Link to={`/catalogue?category=${categoryId}`} className="inline-flex items-center gap-1.5 text-[12px] text-[#9A7B4F]/60 hover:text-[#9A7B4F] transition-colors" data-testid="view-full-collection-link">
            View Full Collection <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}
    </section>
  );
}
